import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useState, useMemo } from "react";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Loader2 } from "lucide-react";

import { useRouteParam } from "@/lib/use-route-param";
import { PageHeader, PageHeaderSkeleton } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TimeRangeSelect } from "@/components/admin/time-range-select";
import { CostBarChart } from "@/components/admin/cost-bar-chart";
import {
  formatCost,
  formatOperationType,
  getTimeRangeBounds,
} from "@/lib/cost";
import type { TimeRange } from "@/lib/cost";

type ProjectDetail = {
  projectName: string;
  totalCost: number;
  totalOps: number;
  typeBreakdown: Array<{ type: string; totalCost: number; opCount: number }>;
  recentOperations: Array<{
    id: string;
    type: string;
    totalCost: number;
    completedAt?: number;
    targetKind?: string;
  }>;
};

export const Route = createFileRoute(
  "/admin/orgs/$orgId/projects/$projectId"
)({
  component: ProjectDetailPage,
});

function ProjectDetailPage() {
  const orgId = useRouteParam("orgId");
  const projectId = useRouteParam("projectId");
  const [timeRange, setTimeRange] = useState<TimeRange>("all");
  const bounds = useMemo(() => getTimeRangeBounds(timeRange), [timeRange]);

  const { data: orgNameData, error: orgNameError } = useQuery(
    convexQuery(
      api.admin.platform.orgName,
      orgId ? { organizationId: orgId as Id<"organizations"> } : "skip"
    )
  );
  const { data: projectDetailData, error: projectDetailError } = useQuery(
    convexQuery(
      api.admin.platform.projectDetail,
      orgId && projectId
        ? { organizationId: orgId as Id<"organizations">, projectId: projectId as Id<"projects">, ...bounds }
        : "skip"
    )
  );

  const orgName = orgNameData as string | undefined;
  const projectDetail = projectDetailData as ProjectDetail | undefined;

  if (!orgId || !projectId) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        Invalid route — organization or project ID is missing.
      </div>
    );
  }

  if (orgNameError || projectDetailError) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        Failed to load project data.
      </div>
    );
  }

  if (projectDetail === undefined || orgName === undefined) {
    return (
      <div className="space-y-4">
        <PageHeaderSkeleton withTrail />
        <div className="flex items-center justify-center py-24">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  const typeBarData = projectDetail.typeBreakdown.map((t) => ({
    name: formatOperationType(t.type),
    rawCost: t.totalCost,
  }));

  return (
    <div className="space-y-4">
      <PageHeader
        title={projectDetail.projectName}
        trail={[
          { label: "Cost Dashboard", href: "/admin" },
          { label: orgName, href: "/admin/orgs/$orgId", params: { orgId } },
        ]}
        actions={
          <TimeRangeSelect value={timeRange} onChange={setTimeRange} />
        }
      />

      {/* Summary */}
      <div className="rounded-lg border-2 border-border bg-card px-6 py-4">
        <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Project Total Cost
        </p>
        <p className="mt-1 text-3xl font-bold tracking-tight text-foreground">
          {formatCost(projectDetail.totalCost)}
        </p>
        <p className="text-sm text-muted-foreground">
          {projectDetail.totalOps} completed operation
          {projectDetail.totalOps !== 1 ? "s" : ""}
        </p>
      </div>

      {/* Cost by Type */}
      {projectDetail.typeBreakdown.length > 0 && (
        <Card className="py-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Cost by Operation Type</CardTitle>
          </CardHeader>
          <CardContent>
            <CostBarChart
              data={typeBarData}
              height={Math.max(150, typeBarData.length * 50)}
            />
          </CardContent>
        </Card>
      )}

      {/* Recent Operations */}
      <Card className="py-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Recent Operations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-bold uppercase tracking-wider text-xs">
                    Type
                  </TableHead>
                  <TableHead className="font-bold uppercase tracking-wider text-xs">
                    Target
                  </TableHead>
                  <TableHead className="font-bold uppercase tracking-wider text-xs text-right">
                    Cost
                  </TableHead>
                  <TableHead className="font-bold uppercase tracking-wider text-xs text-right">
                    Completed
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projectDetail.recentOperations.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="text-center text-muted-foreground py-8"
                    >
                      No operations yet
                    </TableCell>
                  </TableRow>
                ) : (
                  projectDetail.recentOperations.map((op) => (
                    <TableRow key={op.id}>
                      <TableCell className="font-medium">
                        {formatOperationType(op.type)}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {op.targetKind}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCost(op.totalCost)}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {op.completedAt
                          ? new Date(op.completedAt).toLocaleDateString()
                          : "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
